/**
 * Helpers for the shell's `reconnect` query param, which any micro-frontend
 * can set to open the shell-hosted "Reconnect My Accounts" modal.
 *
 * Open/close state for this modal is carried on the URL rather than any
 * cross-app event channel — the same house rule the `event` query param
 * follows (see `eventParam.ts` and the doc comment on `EventDraftPatch` in
 * `eventDraftChannel.ts`): open/close travels on the URL, channels only carry
 * in-flight data once something is already open.
 *
 * All shell code (and any MFE that wants to trigger the modal) MUST go
 * through these helpers rather than reading/writing `reconnect` by hand.
 */
export var RECONNECT_PARAM = 'reconnect';
/**
 * The flag gating the whole feature. Lives here, shared, because the shell and
 * every MFE that offers the button must agree on it exactly: while it is off
 * the shell renders no modal, so an MFE that opened it anyway would set a
 * param nothing answers and leave the user with a button that does nothing.
 */
export var RECONNECT_ACCOUNTS_FLAG = 'chronos(shell-reconnect-accounts)';
/**
 * Whether the reconnect modal should be open, given the current
 * `URLSearchParams`.
 *
 * This checks *presence*, not the value's truthiness. `?reconnect` (no `=`)
 * is a valid "open" URL and `params.get('reconnect')` returns `''` for it —
 * an empty string, which is falsy in JS. A naive `if (params.get(...))`
 * check would read that as closed, which is wrong: `has()` is what tells us
 * the param was set at all. Any future edit here must keep using `has`, not
 * the value.
 */
export var isReconnectOpen = function (params) {
    return params.has(RECONNECT_PARAM);
};
/**
 * Open the reconnect modal by setting the `reconnect` param, mutating
 * `params` in place. Leaves every other param untouched. Safe to call when
 * the param is already set.
 */
export var openReconnectAccounts = function (params) {
    params.set(RECONNECT_PARAM, '1');
};
/**
 * Close the reconnect modal by removing the `reconnect` param, mutating
 * `params` in place. Leaves every other param untouched. Safe to call when
 * the param is already absent.
 */
export var clearReconnectParam = function (params) {
    params.delete(RECONNECT_PARAM);
};
